/**
 * Fill in the blanks — free-text (type) mode player
 */

import * as React from 'react';
import { cn, Input } from '@ai-content-hub/content-kit';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import {
    blankMarkerLengthsInPassageOrder,
    type FITBElementData,
    type FITBAnswer,
    type FITBBlank,
    fitbElementSchema,
} from './schema';
import { renderPassageWithBlanks } from './PassageRenderer';
import { resolveFitbPerBlankOk } from './perBlankFeedback';
import map from 'lodash/map';
import size from 'lodash/size';
import max from 'lodash/max';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

export type FITBPlayerTypeProps = ElementPlayerProps<
    FITBElementData,
    FITBAnswer
> & {
    flashBlankId?: string | null;
};

// Stable default (an inline `= {}` is a new object each render; keeps memo/effect deps stable).
const EMPTY_ANSWER: FITBAnswer = {};

export const FITBPlayerType: React.FC<FITBPlayerTypeProps> = ({
    data,
    answer = EMPTY_ANSWER,
    onAnswer,
    disabled = false,
    showFeedback = false,
    result,
    isReview = false,
    className,
    elementChildLabels,
    onFitbBlankAnchor,
    flashBlankId = null,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? fitbElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const review = showFeedback || isReview;
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false hides explanation/hints.
    const revealCorrect = config?.behavior?.showCorrectAnswer !== false;
    // ELMS-2370: hide answer => no per-blank right/wrong marks (learner choice still shown).
    const perOk = revealCorrect ? resolveFitbPerBlankOk(result, data, answer ?? {}, review) : undefined;
    const markerChWidths = React.useMemo(
        () => blankMarkerLengthsInPassageOrder(data.passage),
        [data.passage]
    );

    const inputWidthCh = (blankIndex: number, blank: FITBBlank | undefined): number => {
        const fromMarker = markerChWidths[blankIndex];
        if (fromMarker != null && fromMarker >= 3) {
            return Math.min(fromMarker, 48);
        }
        if (!blank) return 8;
        const lens = map(blank.acceptedAnswers, (a) => size(String(a)));
        const m = max(lens) ?? 8;
        return Math.min(Math.max(m + 2, 6), 48);
    };

    return (
        <div className={cn('fitb-player fitb-player-type space-y-4', className)}>
            <div className="fitb-player-type-passage rounded-lg border-0 bg-card p-4 shadow-sm">
                {renderPassageWithBlanks(data.passage, {
                    renderBlank: (idx, id) => {
                        const blank = data.blanks[idx];
                        const w = Math.max(inputWidthCh(idx, blank), 15);
                        const val = answer[id] ?? '';
                        const ok = perOk?.[id];
                        const show = review && ok !== undefined;
                        const childN = elementChildLabels?.fitbBlank?.[id];
                        return (
                            <span
                                ref={(el) => onFitbBlankAnchor?.(id, el)}
                                className={cn(
                                    // items-baseline + align-baseline: the numbered badge and the
                                    // input share the text baseline of the surrounding passage
                                    // (was items-center/align-middle, which made them float off it).
                                    // max-w-full keeps a wide blank inside a narrow WebView.
                                    'fitb-player-type-blank inline-flex flex-row items-baseline gap-1 align-baseline mx-0.5 max-w-full',
                                    flashBlankId === id &&
                                        'rounded-sm ring-2 ring-primary/50 ring-offset-1 transition-[box-shadow] duration-300'
                                )}
                            >
                                {childN != null ? (
                                    <span
                                        className="inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-md bg-muted-foreground/15 text-sm font-bold text-foreground"
                                        aria-hidden
                                    >
                                        {childN}
                                    </span>
                                ) : null}
                                {/* Correct-answer hint is a sibling AFTER the input on the same
                                    baseline (wraps beside/below only when it does not fit), so it
                                    no longer stacks under the input and inflates the line height. */}
                                <span className="fitb-player-type-blank-input inline-flex min-w-0 max-w-full flex-row flex-wrap items-baseline gap-x-1.5">
                                    <Input
                                        className={cn(
                                            'fitb-player-type-input inline-block h-8 min-w-[15ch] max-w-full py-1 px-2 align-baseline',
                                            show && ok && 'border-success ring-1 ring-success/40',
                                            show &&
                                                !ok &&
                                                'border-destructive ring-1 ring-destructive/40',
                                            review &&
                                                'disabled:pointer-events-none disabled:!opacity-100 text-foreground',
                                            show && ok && 'text-success',
                                            show && !ok && 'text-destructive'
                                        )}
                                        style={{ width: `${w}ch` }}
                                        value={val}
                                        onChange={(e) =>
                                            onAnswer?.({
                                                ...answer,
                                                [id]: e.target.value,
                                            })
                                        }
                                        disabled={disabled || review}
                                        // EAA-2067: a free-typing input has nothing to "choose" —
                                        // only the author's own per-blank placeholder is shown.
                                        // The aria label follows the visible running number
                                        // (childN) when the composite numbers blanks across items.
                                        placeholder={blank?.placeholder || undefined}
                                        aria-label={`${t('blankNth')} ${childN ?? idx + 1}`}
                                    />
                                    {show && !ok && revealCorrect && blank?.acceptedAnswers?.[0] != null && (
                                        <span className="fitb-player-type-hint text-xs text-muted-foreground">
                                            {String(blank.acceptedAnswers[0])}
                                        </span>
                                    )}
                                </span>
                            </span>
                        );
                    },
                })}
            </div>
            {review && revealCorrect && data.explanation ? (
                <p className="fitb-player-type-explanation text-sm text-muted-foreground border-t border-border pt-3">
                    {data.explanation}
                </p>
            ) : null}
        </div>
    );
};
